"use client";

import { useEffect, useMemo, useState } from "react";
import { useBookmark } from "@/app/hooks/use-bookmark";
import VehicleCard from "@/app/(pages)/(frontend)/t/[tenant]/gebrauchtwagen/components/vehicle-card";
import { fetchBookmarkedVehicles } from "@/app/(pages)/(frontend)/t/[tenant]/gebrauchtwagen/lib/fetch-bookmarked-vehicles";
import type { VehicleRow } from "@/app/(pages)/(frontend)/t/[tenant]/gebrauchtwagen/lib/fetch-vehicle-data";
import LoadingSpinner from "@/app/components/ui/loading-spinner";

/**
 * Client component that displays bookmarked vehicles
 * Fetches only the bookmarked vehicles from the database
 */
export default function BookmarkedVehiclesSection() {
  const { vehicleIds } = useBookmark();
  const [bookmarkedVehicles, setBookmarkedVehicles] = useState<VehicleRow[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);

  // Memoize vehicleIds to prevent unnecessary re-renders
  // Zustand returns a new array reference when IDs change, but we need
  // a stable comparison to avoid excessive fetch requests.
  // We compare by value (sorted IDs) rather than reference.
  const vehicleIdsKey = useMemo(
    () => JSON.stringify([...vehicleIds].sort((a, b) => a - b)),
    [vehicleIds]
  );

  useEffect(() => {
    async function loadBookmarkedVehicles() {
      if (vehicleIds.length === 0) {
        setBookmarkedVehicles([]);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const vehicles = await fetchBookmarkedVehicles(vehicleIds);
        setBookmarkedVehicles(vehicles);
      } catch (error) {
        console.error("Error fetching bookmarked vehicles:", error);
        setBookmarkedVehicles([]);
      } finally {
        setIsLoading(false);
      }
    }

    loadBookmarkedVehicles();

    // Using vehicleIdsKey instead of vehicleIds to prevent re-fetching
    // when only the array reference changes but IDs remain the same
  }, [vehicleIdsKey]);

  if (isLoading) {
    return (
      <div className="my-6 text-center text-gray-500">
        <LoadingSpinner />
      </div>
    );
  }

  if (bookmarkedVehicles.length === 0) {
    return null;
  }

  return (
    <div className="mt-6 grid grid-cols-1 gap-4 md:block md:space-y-6">
      {bookmarkedVehicles.map((vehicle) => (
        <div className="last:mb-6" key={vehicle.vehicleid}>
          <VehicleCard vehicle={vehicle} />
        </div>
      ))}
    </div>
  );
}
